import type { ValidationIssues } from '~/models/common'
import {
  buildStrengthEntryRecord,
  type StrengthEntryInput,
  type StrengthEntryRecord,
} from '~/models/strengthEntry'
import type { CalendarDate } from '~/types/dates'
import { toIsoTimestamp, unwrapDate } from '~/types/dates'
import type { SentinelLiftId, StrengthEntryId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'

function invalidRecord(issues: ValidationIssues): StorageError {
  return storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues)
}

export async function getStrengthEntry(
  db: SolaDatabase,
  id: StrengthEntryId,
): Promise<Result<StrengthEntryRecord | undefined, StorageError>> {
  try {
    return ok(await db.get(STORE.strengthEntries, id))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Every set, oldest first — the `date` index gives ascending order. */
export async function listStrengthEntries(
  db: SolaDatabase,
): Promise<Result<readonly StrengthEntryRecord[], StorageError>> {
  try {
    return ok(await db.getAllFromIndex(STORE.strengthEntries, 'date'))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Every set of one lift within an inclusive date range, oldest first.
 *
 * This is the query the four-week comparison is built on (D-066, D-069), which
 * is why the compound `[liftId, date]` index exists: it bounds the scan to one
 * lift and one window rather than reading every set ever recorded and filtering
 * in memory.
 */
export async function listStrengthEntriesForLift(
  db: SolaDatabase,
  liftId: SentinelLiftId,
  from: CalendarDate,
  to: CalendarDate,
): Promise<Result<readonly StrengthEntryRecord[], StorageError>> {
  try {
    const range = IDBKeyRange.bound(
      [liftId as string, unwrapDate(from)],
      [liftId as string, unwrapDate(to)],
    )
    const rows = await db.getAllFromIndex(STORE.strengthEntries, 'liftId_date', range)
    return ok(rows)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Every set of one lift, ever, oldest first.
 *
 * Still bounded through the compound index rather than read-and-filtered: a
 * lift's whole history is what the "change since start" comparison needs
 * (D-069), but the scan stays confined to that one lift. The upper bound is a
 * character above any date string, which is how a prefix range is expressed
 * against a compound key.
 */
export async function listAllStrengthEntriesForLift(
  db: SolaDatabase,
  liftId: SentinelLiftId,
): Promise<Result<readonly StrengthEntryRecord[], StorageError>> {
  try {
    const range = IDBKeyRange.bound([liftId as string, ''], [liftId as string, '￿'])
    return ok(await db.getAllFromIndex(STORE.strengthEntries, 'liftId_date', range))
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function addStrengthEntry(
  db: SolaDatabase,
  input: StrengthEntryInput,
  now: Date,
): Promise<Result<StrengthEntryRecord, StorageError>> {
  const built = buildStrengthEntryRecord(input, toIsoTimestamp(now))
  if (!built.ok) return err(invalidRecord(built.error))

  try {
    await db.put(STORE.strengthEntries, built.value)
    notifyWritten(STORE.strengthEntries)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/**
 * Correct a recorded set.
 *
 * Several sets of one lift on one date are normal and are kept as separate
 * records — unlike a weight entry, which is one per date and overwritten
 * (D-035). A mistyped load is corrected in place; a set that should not exist is
 * removed explicitly.
 */
export async function updateStrengthEntry(
  db: SolaDatabase,
  id: StrengthEntryId,
  input: StrengthEntryInput,
  now: Date,
): Promise<Result<StrengthEntryRecord, StorageError>> {
  try {
    const existing = await db.get(STORE.strengthEntries, id)
    if (existing === undefined) {
      return err(storageError('invalidRecord', 'That set no longer exists.'))
    }

    const built = buildStrengthEntryRecord(input, toIsoTimestamp(now), {
      id: existing.id,
      createdAt: existing.createdAt,
    })
    if (!built.ok) return err(invalidRecord(built.error))

    await db.put(STORE.strengthEntries, built.value)
    notifyWritten(STORE.strengthEntries)
    return ok(built.value)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Remove one set. Deleting an id that is not present is a no-op, not an error. */
export async function deleteStrengthEntry(
  db: SolaDatabase,
  id: StrengthEntryId,
): Promise<Result<void, StorageError>> {
  try {
    await db.delete(STORE.strengthEntries, id)
    notifyWritten(STORE.strengthEntries)
    return ok(undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}
